import type { ReactNode } from "react";
import { LucideIcon, type LucideIconName } from "./LucideIcon";
import "./accountNudgeBanner.css";

/**
 * Shared presentational "account nudge" banner (E1 consolidation).
 *
 * ONE implementation, TWO messages: `<PromotedSessionBanner>` (an anon device
 * whose session is already promoted to an account) and `<SignedOutBanner>` (a
 * customer who just signed out) render the same amber notice — icon, message,
 * a same-tab sign-in CTA, and a dismiss button. This factors the shared markup
 * and styling so the two banners can't visually drift; each owner still owns its
 * OWN visibility / hint logic and passes the copy + handlers in.
 *
 * The two states are mutually exclusive at the source (AuthProvider sets at most
 * one of `promotedHint` / `signedOut`, promoted-hint winning), so these never
 * stack.
 */
export function AccountNudgeBanner({
  icon,
  message,
  ctaLabel,
  loginUrl,
  onDismiss
}: {
  /** Lucide icon name for the leading glyph. */
  icon: LucideIconName;
  /** The banner copy. */
  message: ReactNode;
  /** Sign-in CTA label. */
  ctaLabel: string;
  /** Storefront login URL (same-tab navigation). */
  loginUrl: string;
  /** Dismiss handler — the owner clears its own hint/flag. */
  onDismiss: () => void;
}) {
  return (
    <div className="fg fg-account-nudge" role="status" aria-live="polite">
      <div className="fg-account-nudge__inner">
        <span className="fg-account-nudge__icon" aria-hidden>
          <LucideIcon name={icon} size={18} strokeWidth={2.1} />
        </span>
        <span className="fg-account-nudge__msg">{message}</span>
        <a className="fg-btn fg-btn--sm fg-btn--primary fg-account-nudge__cta" href={loginUrl}>
          {ctaLabel}
        </a>
        <button
          type="button"
          className="fg-account-nudge__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss">
          <LucideIcon name="x" size={16} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
